import React, { useEffect, useMemo, useState } from 'react';
import PatientModalContainer from 'containers/patient/PatientModalContainer';
import { useQuickstartStore } from 'store/local/quickstart';
import { useQuickStart } from 'lib/hooks/quickstart/useQuickStart';
import styled from 'styled-components';
import PageTitle from 'components/base/title/PageTitle';
import QuickSearch from 'components/quickstart/QuickSearch';
import QuickProjectList from 'components/quickstart/QuickProjectList';
import QuickMenu from 'components/quickstart/QuickMenu';
import PlainModal from 'components/common/modal/PlainModal';
import AppModal from 'components/common/modal/AppModal';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { BaseActions, PatientActions, ProjectActions } from 'store/actionCreators';
import { useDidUpdateEffect } from 'lib/utils';
import storage, { keys } from 'lib/storage';
import { pageUrl } from 'lib/mapper';
import ClinicModal from 'components/common/modal/ClinicModal';
import { T } from 'components/common/text';
import CustomSpan from 'components/common/text/CustomSpan';
import { icon_circle_warning } from 'components/base/images';
import { TRememberPatient } from 'lib/types/patientTypes';

function QuickStartContainer() {
  const { ipcRenderer } = window;
  const { handleClosePatientModal } = useQuickStart();
  const { t } = useTranslation();

  let searchPatientTimer: any = null; // 환자 검색 시 사용할 타이머 변수
  const history = useHistory();
  const [scanProjectData, setScanProjectData] = useState<any>();
  const { search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const [isNoPatientModal, setIsNoPatientModal] = useState<boolean>(false);

  const {
    setPatientData,
    patientData,
    patientModalType,
    isOpenPatientModal,
    searchKeyword,
    setSearchKeyword,
    prevSearchKeyword,
    setPrevSearchKeyword,
    selectedPatientCode,
    setSelectedPatientCode,
    setPatientListLoadSuccess,
    patientList,
    setPatientList,
    isChangeKeyword,
    setIsOpenSearchResult,
    setRememberPatient,
    setProjectList,
    setProjectListLoadSuccess,
    setProjectPage,
    isSearchFocus,
    setScanBtnState,
    setProjectBtnState,
    setEditPatientBtnState,
    setIsNewPatient,
    setButtonClickType,
    buttonClickType,
    reset,
  } = useQuickstartStore();
  const {
    patientNameConvert,
    handleClearSearch,
    handleSearchInit,
    handleSearchPatient,
    fetchPatientList,
    fetchPatientListSuccess,
    fetchProjectList,
    fetchProjectListSuccess,
    createPatient,
    createPatientSuccess,
    patientInfo,
    fetchPatientSuccess,
    fetchPatientFailure,
    createProjectInitialize,
    projectCount,
    projectInitSuccess,
    createProject,
    createProjectData,
    createProjectSuccess,
    editProjectData,
    editProjectSuccess,
    rememberPatientSetStorage,
    handleClickOutsideSearchBar,
    handleSendExcuteApp,
    editPatient,
    editPatientSuccess,
  } = useQuickStart();

  const handleClickNoPatient = () => {
    console.log('handleClickNoPatient ------------');
    const searchParamPatientCode = searchParams.get('patientCode');
    const deleteComparison: TRememberPatient[] =
      storage.get(keys.remember_patient)?.filter((item: TRememberPatient) => {
        return item.code !== searchParamPatientCode;
      }) || [];
    storage.set(keys.remember_patient, deleteComparison);
    setRememberPatient(deleteComparison);

    setIsNoPatientModal(false);
    history.replace(`${pageUrl.quickstart.index}`);
  };

  useEffect(() => {
    const searchParamPatientCode = searchParams.get('patientCode');
    const searchParamPatientName = searchParams.get('name');
    const searchParamPage = searchParams.get('page');
    setProjectList([]);
    setProjectListLoadSuccess(false);
    if (!searchParamPatientCode || !searchParamPatientName || !searchParamPage) {
      handleSearchInit();
      return;
    }
    PatientActions.fetch_patient_request(searchParamPatientCode);
    setSearchKeyword(searchParamPatientName);
    setPrevSearchKeyword(searchParamPatientName);
    setSelectedPatientCode(searchParamPatientCode);
    setProjectPage(Number(searchParamPage));
    ProjectActions.fetch_projects_request({
      page: searchParamPage,
      patientCode: selectedPatientCode,
    });
  }, [searchParams, selectedPatientCode]);

  useDidUpdateEffect(() => {
    if (!fetchProjectListSuccess) return;
    setProjectListLoadSuccess(true);
    setProjectList(fetchProjectList);
  }, [fetchProjectListSuccess, fetchProjectList]);

  useEffect(() => {
    if (!isChangeKeyword) return;
    if (searchKeyword.length > 0) {
      setIsOpenSearchResult(true);
      searchPatientTimer = setTimeout(() => {
        // TODO: 검색 시작 시, prev 저장 시도
        handleSearchPatient(searchKeyword);
      }, 500);
      return () => {
        clearTimeout(searchPatientTimer);
      };
    } else {
      handleClearSearch();
    }
  }, [searchKeyword, isChangeKeyword]);

  useDidUpdateEffect(() => {
    if (!fetchPatientListSuccess) return;
    setPatientListLoadSuccess(true);
    setPatientList([...patientList, ...fetchPatientList]);
  }, [fetchPatientListSuccess, fetchPatientList]);

  useEffect(() => {
    const remember_patients = storage.get(keys.remember_patient) || [];
    console.log('HDS] remember_patients __ ', remember_patients);
    if (!remember_patients || remember_patients === undefined) {
      setRememberPatient([]);
    } else {
      setRememberPatient([...remember_patients]);
    }
  }, []);

  useEffect(() => {
    if (!!isSearchFocus && !!searchKeyword) {
      console.log('HDS] QuickMenu _________ 검색 o');
      setScanBtnState(true);
      setProjectBtnState(true);
      setEditPatientBtnState(false);
      setIsNewPatient(true);
    } else if (!isSearchFocus && !!selectedPatientCode) {
      console.log('HDS] QuickMenu _________ 환자 선택 o');
      setScanBtnState(true);
      setProjectBtnState(true);
      setEditPatientBtnState(true);
      setIsNewPatient(false);
    } else {
      setScanBtnState(false);
      setProjectBtnState(false);
      setEditPatientBtnState(false);
      setIsNewPatient(false);
    }
  }, [isSearchFocus, selectedPatientCode, searchKeyword]);

  useDidUpdateEffect(() => {
    if (!createPatientSuccess) return;
    const patientName = patientNameConvert(createPatient);
    setPatientData(createPatient);
    history.replace(
      `${pageUrl.quickstart.index}?patientCode=${createPatient.patientCode}&name=${patientName}&page=1`,
    );
    if (!buttonClickType) {
      setSearchKeyword(patientName);
      setSelectedPatientCode(createPatient.patientCode);
      rememberPatientSetStorage({ name: patientName, code: createPatient.patientCode });
    } else {
      createProjectInitialize();
    }
  }, [createPatientSuccess, createPatient, buttonClickType]);

  useDidUpdateEffect(() => {
    if (!fetchPatientSuccess) return;
    setPatientData(patientInfo);
  }, [fetchPatientSuccess, patientInfo]);

  useDidUpdateEffect(() => {
    if (!fetchPatientFailure) return;
    setIsNoPatientModal(true);
  }, [fetchPatientFailure]);

  useDidUpdateEffect(() => {
    if (!projectInitSuccess) return;
    createProject(patientData, projectCount);
  }, [projectInitSuccess, patientData, projectCount]);

  useDidUpdateEffect(() => {
    if (!!createProjectSuccess && !!patientData) {
      if (buttonClickType === 'scan') {
        // setScanProjectData(editProjectData);
        // BaseActions.is_re_scan(false);
        // handleSendExcuteApp({
        //   appName: 'APP_SCAN_IOS',
        //   projectCode: editProjectData.projectInformation.projectCode,
        //   projectId: editProjectData.projectInformation.projectId,
        // });
        // BaseActions.is_re_scan(true);
        console.log("HDS] buttonClickType === 'scan' ");
        BaseActions.is_re_scan(false);
        history.push(
          `/project/creating/${createProjectData.projectInformation.projectCode}?pathTo=detail&scanStart=1`,
        );
      } else {
        console.log("HDS] buttonClickType !== 'scan' ");
        const patientName = patientNameConvert(patientData);
        rememberPatientSetStorage({ name: patientName, code: patientData.patientCode });
        history.push(
          `/project/creating/${createProjectData.projectInformation.projectCode}?pathTo=detail`,
        );
      }
    }
  }, [createProjectSuccess, buttonClickType, patientData, createProjectData]);

  useDidUpdateEffect(() => {
    if (!editPatientSuccess) return;
    history.replace(
      `${pageUrl.quickstart.index}?patientCode=${editPatient.patientCode}&name=${
        editPatient.firstName
      }&page=${1}`,
    );
  }, [editPatientSuccess]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutsideSearchBar);
    return () => {
      document.removeEventListener('mousedown', handleClickOutsideSearchBar);
    };
  }, [prevSearchKeyword, searchKeyword, searchParams]);

  useEffect(() => {
    console.log('QUICKSTART 진입 ------------');
    return () => {
      reset();
      PatientActions.fetch_patient_clear();
      ProjectActions.fetch_projects_clear();
    };
  }, []);

  return (
    <StyledQuickStart data-component-name="QuickStart">
      <PageTitle title={t('NAV_MENU_QUICKSTART')} className="quickStart__title_box" />
      <div className="quickStart__container">
        <div className="quickStart__search_wrapper">
          <QuickSearch />
          {!!selectedPatientCode && <QuickProjectList />}
        </div>
        <div className="quickStart__menu_wrapper">
          <QuickMenu />
        </div>
      </div>
      <PlainModal isOpen={isOpenPatientModal} onClose={handleClosePatientModal} width={1000}>
        <AppModal
          title={'Patient'}
          content={
            <PatientModalContainer
              name={searchKeyword}
              type={patientModalType}
              ptCode={patientModalType === 'edit' ? selectedPatientCode : ''}
              onCloseModal={handleClosePatientModal}
            />
          }
          contentCardStyle={{
            padding: '0px',
            backgroundColor: 'transparent',
          }}
          isCloseIcon={true}
          onClick={handleClosePatientModal}
          onCancel={handleClosePatientModal}
          hideButton={true}
        />
      </PlainModal>
      <ClinicModal
        title={t('CLINIC_MODAL_TITLE_NOTICE')}
        isOpen={isNoPatientModal}
        onClose={handleClickNoPatient}
        // onCancel={handleClickNoPatient}
        onConfirm={handleClickNoPatient}
      >
        <NoPatientModal />
      </ClinicModal>
    </StyledQuickStart>
  );
}

const StyledQuickStart = styled.section<{}>`
  .quickStart__container {
    width: 100%;
    height: 100%;
    margin-top: 10px;
    display: flex;
    justify-content: space-between;
    .quickStart__search_wrapper {
    }
  }
`;

interface NoPatientModalProps {}
function NoPatientModal({}: NoPatientModalProps) {
  return (
    <StyledNoPatientModal data-component-name="NoPatientModal">
      <div className="noPatientModal__description">
        <p className="noPatientModal__description_noti">
          <img src={icon_circle_warning} />
          <CustomSpan>
            <T>NOT_FOUND_PATIENT</T>
          </CustomSpan>
        </p>
      </div>
    </StyledNoPatientModal>
  );
}

const StyledNoPatientModal = styled.section<{}>`
  .noPatientModal__description {
    white-space: break-spaces;
    display: flex;
    flex-direction: column;
    row-gap: 10px;
    padding-bottom: 20px;
    .noPatientModal__description_noti {
      display: flex;
      column-gap: 5px;
      align-items: center;
    }
  }
`;

export default React.memo(QuickStartContainer);
